/**
 * Minimal RFC 4180 CSV parser — enough for operator-supplied vendor
 * imports without pulling in a dependency. Handles quoted fields, escaped
 * quotes (`""`), embedded commas/newlines, and both `\n` and `\r\n` line
 * endings. Returns a rectangular-ish array of string rows (each row is the
 * raw cells; the caller maps headers → fields).
 *
 * A leading UTF-8 BOM (Excel writes one) is stripped so the first header
 * doesn't silently gain a `﻿` prefix.
 */
export function parseCsv(input: string): string[][] {
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++; // consume the escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      // Swallow a \r\n pair as one break.
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }

  // Flush the trailing cell/row (files without a terminating newline).
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  // Drop fully-blank rows (trailing newline, empty lines between records).
  return rows.filter((r) => r.some((c) => c.trim().length > 0));
}
